// MACHINERY V2 (Stroje) — the fleet's PER-SECTION SESSION STATE (split out of
// FleetPage.tsx at M2.16a).
//
// >>> WHY THIS FILE EXISTS. <<< FleetPage.tsx stood at 295 of the project's 300
// lines and M2.16 must ADD to it. The near-cap rule makes the split a discrete
// FIRST task, before any feature line lands: M2.13 was granted the one near-cap
// exemption, on the explicit ruling that the next edit splits first and there
// is no second one. FleetContent.tsx, FleetOverlays.tsx and FleetShell.tsx were
// the earlier cuts out of this page; this is M2.16a's.
//
// THE SEAM: everything whose lifetime is the CURRENT SECTIONING. Which groups
// are collapsed, how the V3 table is ordered, and the group-by change that
// resets both — one dimension change, one reset, one file. What stays in
// FleetPage is the state that is NOT sectioning-shaped (scope, the readiness
// machine, the add picker) and every callback that navigates or fetches.
//
// >>> IT IS A HOOK THE PAGE CALLS, NOT A COMPONENT. <<< The state still lives
// in FleetPage's own instance and has exactly the lifetime it had before this
// split — which is the entire point of it being page-level. A body (V1 cards,
// V2 rows, V3 table, V4 wall) UNMOUNTS on every view toggle, so a collapse set
// or a sort order owned by a body would silently reset on a V3 -> V1 -> V3
// round trip. Call this from FleetPage and NOWHERE else: calling it from a body
// re-creates the exact bug the placement prevents, and calling it twice would
// give the two callers divergent collapse sets.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import { useCallback, useState } from "react";

import { useAppDispatch } from "../../../../shared/hooks/sharedHooks";
import { MachineryV2GroupBy, setFleetGroupBy } from "../machineryV2UiSlice";
import {
  DEFAULT_FLEET_TABLE_SORT,
  FleetTableSort,
  FleetTableSortColumn,
  nextSort,
} from "./table/fleetTableSort";

export interface FleetSectionState {
  /** Keyed by the RAW section key, shared by every view — see `toggleGroup`. */
  collapsedGroups: Set<string>;
  toggleGroup: (name: string) => void;
  /** V3 only; held here so a V1/V3 round trip keeps the ordering. */
  tableSort: FleetTableSort;
  onSortChange: (column: FleetTableSortColumn) => void;
  /** Not `setFleetGroupBy` directly — changing the dimension also resets the
   *  collapse set and the table sort. */
  changeGroupBy: (groupBy: MachineryV2GroupBy) => void;
}

export const useFleetSectionState = (): FleetSectionState => {
  const dispatch = useAppDispatch();

  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  /**
   * M2.13 — the V3 table's column sort. SESSION state, held HERE and not in the
   * table, for the same reason `collapsedGroups` is: a body unmounts on every
   * view toggle, so sort state living inside the table would silently reset on
   * a V3 -> V1 -> V3 round trip. It is deliberately NOT in the slice either —
   * ruling (iii) persists the VIEW MODE only; filters and orderings stay
   * session-only. Reset semantics: see changeGroupBy.
   */
  const [tableSort, setTableSort] = useState(DEFAULT_FLEET_TABLE_SORT);

  // Collapse state is keyed by the RAW section key and is shared between the
  // views — since ruling (i) they all section from the SAME `fleetGroupBy` map,
  // so a group collapsed in V1 is the same group in V2 and V3. Reset on a
  // group-by change (see changeGroupBy).
  const toggleGroup = useCallback((name: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  /**
   * REVIEW F2 — changing the dimension MUST clear the collapse set, exactly as
   * the live grid does (Machinery.tsx:759-761). The set is keyed by section
   * TITLE, and groupMachinesClientSide emits the same sentinel `"Ungrouped"`
   * for location, line AND department — so collapsing "Ungrouped" under
   * Group-by Line and switching to Group-by Location would render a DIFFERENT
   * section already collapsed with no user action. Any group name shared
   * across two dimensions collides the same way.
   *
   * M2.13 — THE TABLE SORT RESETS ON THE SAME EVENT, deliberately. Sorting is
   * per-section (each group is its own <table>), so an ordering the user chose
   * is an ordering of sections that no longer exist after a dimension change.
   * Binding both resets to this one event keeps ONE mental model — "changing
   * how the fleet is divided resets how it is arranged" — instead of two
   * half-remembered ones. Everything else preserves the sort: filters, search,
   * scope, the 60 s telemetry tick and a V1/V2/V3 round trip all leave it
   * alone, because that is the ordering the user is actively reading.
   */
  const changeGroupBy = useCallback(
    (g: MachineryV2GroupBy) => {
      dispatch(setFleetGroupBy(g));
      setCollapsedGroups(new Set());
      setTableSort(DEFAULT_FLEET_TABLE_SORT);
    },
    [dispatch]
  );

  const onSortChange = useCallback(
    (column: FleetTableSortColumn) => setTableSort((prev) => nextSort(prev, column)),
    []
  );

  return { collapsedGroups, toggleGroup, tableSort, onSortChange, changeGroupBy };
};

export default useFleetSectionState;
